/**
 * Shared My Notes surface for every hub module.
 *
 * Live PHP equivalent: `hub/<module>/pages/my-notes.php` (one per module).
 * Per memory `hub_my_notes_unified`, every module talks to the same
 * canonical `/api/notes` endpoint on the shared `user_notes` table — so
 * this is ONE component parameterised by `mod` for accent + kicker +
 * `data-mod` styling. No per-module note tables (`lemax_notes` etc. are
 * deprecated and must never be reintroduced).
 *
 * ── 2026-07-31 rebuild ────────────────────────────────────────────────
 * Six defects closed (spec Part B · B5):
 *   1. `listNotes` no longer swallows every error into `[]`. A 500, a
 *      dropped network or an expired session used to render as "No notes
 *      yet. Hit new note to drop your first sticky" — someone with 40
 *      notes saw an empty board and could reasonably start retyping them.
 *      The component's own `isError` branch was unreachable dead code.
 *   2. Delete is deferred behind a 5-second Undo instead of firing on the
 *      first tap of a 26px button sitting next to the pin toggle. Pending
 *      deletes flush on unmount so navigating away still honours them.
 *   3. Delete failures surface (they were completely silent).
 *   4. Pin is optimistic with rollback (it used to be a fire-and-forget
 *      `void` promise that visibly did nothing until the round-trip).
 *   5. `sort_order` is live: notes drag-to-reorder inside their section
 *      and persist through `POST /api/notes/reorder`. The column existed
 *      on the type and was never read or written.
 *   6. The editor uses the canonical opaque `.hub-scrim` / `.hub-modal`
 *      surfaces instead of a hand-rolled translucent `color-mix` scrim.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type TouchEvent as ReactTouchEvent,
} from 'react';
import { createPortal } from 'react-dom';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, apiFetch, apiFetchRaw } from '@/auth/api';
import { moduleAccent, type ModuleSlug } from '@/styles/tokens';
import { DEFAULT_MODULE_TREE } from '@/shared/modules/catalog';
import { moduleLabel, moduleNavLabel, useModuleTree } from '@/shared/modules/store';
import { Button } from '@/shared/components/Button';
import { useLiveAnnounce } from '@/shared/components/LiveAnnouncer';
import { useFocusTrap } from '@/shared/hooks/useFocusTrap';
import { usePrefersReducedMotion } from '@/shared/hooks/usePrefersReducedMotion';
import { planMove, planToItems, type Positioned } from './hotplate/ordering';
import './mynotes.css';

// ── Types ───────────────────────────────────────────────────────────

export interface UserNote {
  id: string | null;
  title: string | null;
  content: string | null;
  color: string | null;
  pinned: boolean | null;
  sort_order: number | null;
  created_at: string | null;
  updated_at: string | null;
}

export const NOTE_COLORS = [
  'yellow',
  'blue',
  'green',
  'pink',
  'purple',
  'orange',
  'red',
  'teal',
  'gray',
] as const;
export type NoteColor = (typeof NOTE_COLORS)[number];

export interface SaveNoteRequest {
  id?: string | null;
  title?: string | null;
  content?: string | null;
  color?: NoteColor | null;
  pinned?: boolean | null;
}

interface NotesEnvelope {
  ok?: boolean;
  notes?: UserNote[];
}

const UNDO_WINDOW_MS = 5000;

// ── API (canonical /api/notes endpoint) ─────────────────────────────

// Wire shapes (NotesController.cs):
//   GET  /api/notes         → {ok, notes: UserNote[]}  ← non-canonical
//                                                        envelope, needs
//                                                        apiFetchRaw
//   POST /api/notes         → {ok, data: UserNote}     ← canonical, apiFetch
//   POST /api/notes/reorder → {ok: true}, body is a BARE array
//   DELETE /api/notes/{id}  → {ok: true}
async function listNotes(signal?: AbortSignal): Promise<UserNote[]> {
  // No try/catch. A failure must reach React Query so the component's
  // error branch renders — swallowing it into `[]` is what made a broken
  // session look like an empty notebook.
  const env = await apiFetchRaw<NotesEnvelope>('/notes', { method: 'GET', signal });
  return env?.notes ?? [];
}

async function saveNote(req: SaveNoteRequest): Promise<UserNote | null> {
  const saved = await apiFetch<UserNote>(
    '/notes',
    {
      method: 'POST',
      body: JSON.stringify({
        Id: req.id ?? null,
        Title: req.title ?? '',
        Content: req.content ?? '',
        Color: req.color ?? 'yellow',
        Pinned: req.pinned ?? false,
      }),
    },
    { mutation: true },
  );
  return saved ?? null;
}

async function deleteNote(id: string): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/notes/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    { mutation: true },
  );
}

export interface ReorderResult {
  updated: number;
  /**
   * Ids whose UPDATE matched zero rows. The endpoint used to swallow every
   * per-row failure into a blanket `{ok:true}`, so a partial failure left the
   * optimistic order sitting on top of a DB that disagreed and nothing said
   * so until a refetch shuffled the wall.
   */
  failed: string[];
}

async function reorderNotes(
  items: { id: string; sortOrder: number }[],
): Promise<ReorderResult> {
  if (items.length === 0) return { updated: 0, failed: [] };
  const body = await apiFetchRaw<{ ok?: boolean; updated?: number; failed?: string[] }>(
    '/notes/reorder',
    {
      method: 'POST',
      // The controller binds `List<ReorderItem>` — a BARE array, not an
      // `{items: […]}` envelope.
      body: JSON.stringify(items.map((i) => ({ Id: i.id, SortOrder: i.sortOrder }))),
    },
    { mutation: true },
  );
  return { updated: body?.updated ?? 0, failed: body?.failed ?? [] };
}

// ── Palette ─────────────────────────────────────────────────────────

interface NotePalette {
  bg: string;
  border: string;
  dot: string;
  title: string;
  body: string;
}

function paletteFor(hex: string): NotePalette {
  return {
    bg: `color-mix(in oklab, ${hex} 14%, var(--bg-card))`,
    border: `color-mix(in oklab, ${hex} 35%, var(--border))`,
    dot: hex,
    title: 'var(--text)',
    body: 'var(--text-muted)',
  };
}

const NOTE_PALETTE: Record<string, NotePalette> = {
  yellow: paletteFor('var(--q-cat-gold, #D9B24C)'),
  blue: paletteFor('var(--q-cat-blue, #5B8DEF)'),
  green: paletteFor('var(--q-cat-green, #3CB897)'),
  pink: paletteFor('var(--q-cat-pink, #F195B5)'),
  purple: paletteFor('var(--q-cat-violet, #B57AF2)'),
  orange: paletteFor('var(--q-cat-orange, #E2774F)'),
  red: paletteFor('var(--q-cat-rose, #F26B7E)'),
  teal: paletteFor('var(--q-cat-cyan, #35B0C2)'),
  gray: {
    bg: 'var(--bg-card)',
    border: 'var(--border)',
    dot: 'var(--text-muted)',
    title: 'var(--text)',
    body: 'var(--text-muted)',
  },
};

function notePalette(color: string | null | undefined): NotePalette {
  const fallback = NOTE_PALETTE['yellow'] as NotePalette;
  return NOTE_PALETTE[color ?? 'yellow'] ?? fallback;
}

/** Editor surfaces are opaque; only the card tint carries the note colour. */
function editorTint(color: string | null | undefined): string {
  const pal = notePalette(color);
  return `color-mix(in oklab, ${pal.dot} 10%, var(--surface-2))`;
}

// ── Module → kicker label ───────────────────────────────────────────

export const MODULE_LABEL: Record<ModuleSlug, string> = {
  coportal: 'cs/ops portal',
  restools: 'res tools',
  lemax: 'lemax',
  contracting: 'contracting',
  hr: 'hr',
  it: 'it',
  finance: 'finance',
  data: 'data',
  ecommerce: 'ecommerce',
  bizdev: 'bizdev',
  product: 'product',
  management: 'management',
  aiaiai: 'ai ai ai',
  admin: 'admin',
  preferences: 'preferences',
  'guest-portal': 'guest portal',
};

// ── Ordering ────────────────────────────────────────────────────────

/**
 * Client-side twin of the server's `pinned.desc, sort_order.asc,
 * created_at.desc`. Ties break on id so an optimistic write can never
 * shuffle two equal-`sort_order` notes between renders.
 */
export function compareNotes(a: UserNote, b: UserNote): number {
  const sa = a.sort_order ?? 0;
  const sb = b.sort_order ?? 0;
  if (sa !== sb) return sa - sb;
  const ca = a.created_at ?? '';
  const cb = b.created_at ?? '';
  if (ca !== cb) return ca < cb ? 1 : -1; // newest first
  const ia = a.id ?? '';
  const ib = b.id ?? '';
  return ia < ib ? -1 : ia > ib ? 1 : 0;
}

// ── Editor state ────────────────────────────────────────────────────

interface EditorState {
  open: boolean;
  note: UserNote | null;
}
const CLOSED_EDITOR: EditorState = { open: false, note: null };

// ── Component ───────────────────────────────────────────────────────

/**
 * `mod` drives accent + kicker + `data-mod` styling only — the notes
 * themselves are user-scoped and identical everywhere (`/api/notes`).
 *
 * It is optional because My Notes was hoisted to the top bar (2026-07-29)
 * and now also mounts at the module-less `/my-notes`. There it falls back
 * to the CS/OPS Portal accent rather than rendering unstyled; the
 * per-module routes still pass their own slug and look exactly as before.
 *
 * The query key is deliberately NOT keyed on `mod` — it was, which meant
 * the same notes were cached separately per module and an edit made under
 * /bizdev/my-notes left a stale copy under /hr/my-notes until refetch.
 */
export function ModuleMyNotesPage({ mod = 'coportal' }: { mod?: ModuleSlug }) {
  const qc = useQueryClient();
  const announce = useLiveAnnounce();
  const queryKey = ['my-notes'] as const;
  const [editor, setEditor] = useState<EditorState>(CLOSED_EDITOR);
  const [banner, setBanner] = useState<string | null>(null);

  const notesQuery = useQuery({
    queryKey,
    queryFn: ({ signal }) => listNotes(signal),
    staleTime: 15_000,
  });

  const all = useMemo(() => notesQuery.data ?? [], [notesQuery.data]);

  const { pinned, others } = useMemo(() => {
    const p: UserNote[] = [];
    const o: UserNote[] = [];
    for (const n of all) (n.pinned ? p : o).push(n);
    p.sort(compareNotes);
    o.sort(compareNotes);
    return { pinned: p, others: o };
  }, [all]);

  const writeCache = useCallback(
    (next: (rows: UserNote[]) => UserNote[]) => {
      qc.setQueryData<UserNote[]>(queryKey, (rows) => next(rows ?? []));
    },
    [qc],
  );
  const snapshot = useCallback(() => qc.getQueryData<UserNote[]>(queryKey), [qc]);

  // One timer, cleared on unmount — the previous version leaked a 9-second
  // `setTimeout` per banner that called `setBanner` on a component that may
  // already be gone.
  const bannerTimerRef = useRef<number | null>(null);
  const showBanner = useCallback(
    (msg: string) => {
      setBanner(msg);
      announce(msg);
      if (bannerTimerRef.current !== null) window.clearTimeout(bannerTimerRef.current);
      bannerTimerRef.current = window.setTimeout(() => {
        setBanner(null);
        bannerTimerRef.current = null;
      }, 9000);
    },
    [announce],
  );
  useEffect(
    () => () => {
      if (bannerTimerRef.current !== null) window.clearTimeout(bannerTimerRef.current);
    },
    [],
  );

  const describe = (err: unknown, fallback: string) =>
    err instanceof ApiError || err instanceof Error ? err.message : fallback;

  // ── Delete with a 5-second Undo ────────────────────────────────────
  const pendingDeletes = useRef(new Map<string, { note: UserNote; timer: number }>());
  const [undoNote, setUndoNote] = useState<UserNote | null>(null);

  const flushDelete = useCallback(
    (id: string) => {
      pendingDeletes.current.delete(id);
      setUndoNote((cur) => (cur?.id === id ? null : cur));
      void deleteNote(id).catch((err: unknown) => {
        qc.invalidateQueries({ queryKey });
        showBanner(describe(err, 'Could not delete the note.'));
      });
    },
    [qc, showBanner],
  );

  const requestDelete = useCallback(
    (note: UserNote) => {
      const id = note.id;
      if (!id) return;
      writeCache((rows) => rows.filter((n) => n.id !== id));
      const timer = window.setTimeout(() => flushDelete(id), UNDO_WINDOW_MS);
      pendingDeletes.current.set(id, { note, timer });
      setUndoNote(note);
      announce('Note removed. Undo available for 5 seconds.');
    },
    [announce, flushDelete, writeCache],
  );

  const undoDelete = useCallback(() => {
    const note = undoNote;
    if (!note?.id) return;
    const pending = pendingDeletes.current.get(note.id);
    if (!pending) return;
    window.clearTimeout(pending.timer);
    pendingDeletes.current.delete(note.id);
    setUndoNote(null);
    writeCache((rows) => (rows.some((n) => n.id === note.id) ? rows : [...rows, note]));
    announce('Note restored.');
  }, [announce, undoNote, writeCache]);

  // The note was already gone from the UI, so honour the delete rather
  // than silently resurrecting it on the next load.
  useEffect(() => {
    const pend = pendingDeletes.current;
    return () => {
      for (const [id, p] of pend) {
        window.clearTimeout(p.timer);
        void deleteNote(id).catch(() => {
          /* best effort on unmount */
        });
      }
      pend.clear();
    };
  }, []);

  // ── Pin · optimistic with rollback ─────────────────────────────────
  const pinMut = useMutation({
    mutationFn: (n: UserNote) =>
      saveNote({
        id: n.id,
        title: n.title,
        content: n.content,
        color: (n.color as NoteColor) ?? 'yellow',
        pinned: !n.pinned,
      }),
    onMutate: async (n) => {
      await qc.cancelQueries({ queryKey });
      const prev = snapshot();
      writeCache((rows) =>
        rows.map((r) => (r.id === n.id ? { ...r, pinned: !r.pinned } : r)),
      );
      return { prev };
    },
    onError: (err, _n, ctx) => {
      if (ctx?.prev) qc.setQueryData(queryKey, ctx.prev);
      showBanner(describe(err, 'Could not change the pin.'));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey }),
  });

  // ── Reorder · optimistic, persisted through /notes/reorder ─────────
  //
  // The rollback snapshot lives in `onMutate`/`context` rather than being
  // closed over at the call site — the previous version declared an
  // `onError(err, _v, ctx)` that read `ctx.prev`, but nothing ever put `prev`
  // in the context (there was no `onMutate`), so that handler could only ever
  // show the banner. The real revert hung off a second, per-call `onError`
  // that silently REPLACED it, meaning a failed reorder reverted but never
  // said why.
  const reorderMut = useMutation({
    mutationFn: (vars: {
      items: { id: string; sortOrder: number }[];
      prev: UserNote[] | undefined;
    }) => reorderNotes(vars.items),
    onSuccess: (res) => {
      if (res.failed.length > 0) {
        showBanner(
          `${res.failed.length} note${res.failed.length > 1 ? 's' : ''} could not be reordered — refreshing.`,
        );
        qc.invalidateQueries({ queryKey });
      }
    },
    onError: (err, vars) => {
      if (vars.prev) qc.setQueryData(queryKey, vars.prev);
      else qc.invalidateQueries({ queryKey });
      showBanner(describe(err, 'Could not save the new order.'));
    },
  });

  /**
   * Plan one drop through the Hot Plate's midpoint planner instead of
   * renumbering the whole section.
   *
   * This used to be `arrayMove` + `(i + 1) * SORT_STEP` over every row —
   * the exact whole-column rewrite `hotplate/ordering.ts` was written to
   * replace. `NotesController.Reorder` loops one `await UpdateAsync` per
   * item and `List` caps at 200, so a single drag in the unpinned section
   * could serialise 200 PostgREST PATCHes inside one HTTP request.
   *
   * The planner is generic over `{id, position}`, so `sort_order` maps onto
   * `position` verbatim: the common drop becomes a ONE-row patch, and the
   * full renumber only fires when the integer gap between neighbours is
   * exhausted (~10 consecutive drops into the identical slot).
   */
  const applyReorder = useCallback(
    (section: UserNote[], fromId: string, toId: string) => {
      const withIds = section.filter((n): n is UserNote & { id: string } => !!n.id);
      const from = withIds.findIndex((n) => n.id === fromId);
      const to = withIds.findIndex((n) => n.id === toId);
      if (from < 0 || to < 0 || from === to) return;

      const positioned: Positioned[] = withIds.map((n) => ({
        id: n.id,
        position: n.sort_order ?? 0,
      }));
      // Dragging DOWN lands after the card you dropped onto; dragging UP
      // lands before it. That is the `arrayMove(from, to)` this replaces.
      const plan = planMove(positioned, fromId, toId, from < to ? 'after' : 'before', true);
      // `planToItems` speaks the Hot Plate's `{id, columnKey, position}`
      // wire shape; notes have no columns, so the key is a throwaway and
      // only `position` (→ `sort_order`) survives the mapping.
      const items = planToItems(plan, fromId, 'todo').map(({ id, position }) => ({
        id,
        sortOrder: position,
      }));
      if (items.length === 0) return;

      const prev = snapshot();
      const byId = new Map(items.map((i) => [i.id, i.sortOrder] as const));
      writeCache((rows) =>
        rows.map((r) => {
          const so = r.id ? byId.get(r.id) : undefined;
          return so === undefined ? r : { ...r, sort_order: so };
        }),
      );
      reorderMut.mutate({ items, prev });
      announce('Note order saved.');
    },
    [announce, reorderMut, snapshot, writeCache],
  );

  // ── DnD ────────────────────────────────────────────────────────────
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 220, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const [activeId, setActiveId] = useState<string | null>(null);
  const activeNote = activeId ? (all.find((n) => n.id === activeId) ?? null) : null;
  // The DragOverlay drop animation is an inline JS style, so the CSS
  // `prefers-reduced-motion` block in mynotes.css cannot reach it.
  const reducedMotion = usePrefersReducedMotion();

  const onDragStart = (e: DragStartEvent) => setActiveId(String(e.active.id));
  const onDragEnd = (e: DragEndEvent) => {
    setActiveId(null);
    const from = String(e.active.id);
    const to = e.over ? String(e.over.id) : null;
    if (!to || from === to) return;
    // Reordering only makes sense inside a section — the two sections are
    // separated by `pinned`, which the pin toggle owns.
    const section = pinned.some((n) => n.id === from) ? pinned : others;
    if (!section.some((n) => n.id === to)) return;
    applyReorder(section, from, to);
  };

  // Bespoke drag announcements. dnd-kit's defaults read out the raw
  // draggable id — a uuid — so a keyboard/screen-reader user heard
  // "Draggable item 4f3c… was moved over droppable area 9a01…" instead of
  // the note they are holding. The Hot Plate got purpose-built strings; this
  // board is the same interaction and needs the same treatment.
  const noteTitleOf = useCallback(
    (id: string): string => {
      const n = all.find((x) => x.id === id);
      const t = (n?.title ?? '').trim();
      if (t) return t;
      const body = (n?.content ?? '').trim().split('\n')[0] ?? '';
      return body ? (body.length > 40 ? `${body.slice(0, 40)}…` : body) : 'Untitled note';
    },
    [all],
  );
  const sectionNameOf = useCallback(
    (id: string): string => (pinned.some((n) => n.id === id) ? 'pinned' : 'notes'),
    [pinned],
  );
  const announcements: Announcements = useMemo(
    () => ({
      onDragStart: ({ active }) => {
        const id = String(active.id);
        return `Picked up ${noteTitleOf(id)} in ${sectionNameOf(id)}. Use the arrow keys to move it, space to drop, escape to cancel.`;
      },
      onDragOver: ({ active, over }) => {
        const id = String(active.id);
        if (!over) return `${noteTitleOf(id)} is no longer over a note.`;
        return `${noteTitleOf(id)} is over ${noteTitleOf(String(over.id))}.`;
      },
      onDragEnd: ({ active, over }) => {
        const id = String(active.id);
        if (!over) return `${noteTitleOf(id)} was dropped back where it started.`;
        return `${noteTitleOf(id)} dropped after ${noteTitleOf(String(over.id))} in ${sectionNameOf(id)}.`;
      },
      onDragCancel: ({ active }) =>
        `Move cancelled. ${noteTitleOf(String(active.id))} returned to its position.`,
    }),
    [noteTitleOf, sectionNameOf],
  );

  const [portalReady, setPortalReady] = useState(false);
  useEffect(() => setPortalReady(true), []);

  const styleVars = { ['--accent' as string]: moduleAccent[mod] } as CSSProperties;
  // A module the tenant renamed (Admin ▸ Modules) reads its new name.
  const tree = useModuleTree();
  const label =
    moduleLabel(mod, tree) === moduleLabel(mod, DEFAULT_MODULE_TREE)
      ? MODULE_LABEL[mod]
      : moduleNavLabel(mod, tree).toLowerCase();

  return (
    <div data-mod={mod} style={styleVars} className="mn-root">
      <section style={heroWrap}>
        <div style={{ minWidth: 0 }}>
          <span style={kicker}>{label} · my notes</span>
          <h1 style={heroTitle}>My notes</h1>
          <p style={heroSub}>
            Personal sticky notes — visible only to you. Pinned notes float
            to the top. Colour-code, pin, edit inline, drag to reorder.
          </p>
        </div>
        <Button type="button" onClick={() => setEditor({ open: true, note: null })}>
          new note
        </Button>
      </section>

      {banner && (
        <div role="alert" style={bannerStyle}>
          <span>{banner}</span>
          <button
            type="button"
            onClick={() => setBanner(null)}
            aria-label="Dismiss"
            style={bannerDismiss}
          >
            ×
          </button>
        </div>
      )}

      {notesQuery.isLoading ? (
        <Surface>
          <span style={mutedKicker}>loading notes.</span>
        </Surface>
      ) : notesQuery.isError ? (
        <Surface>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <span style={mutedKicker}>§ could not load</span>
            <p style={emptyBody}>
              {notesQuery.error instanceof Error
                ? notesQuery.error.message
                : 'Could not load notes.'}
            </p>
            <div>
              <Button type="button" onClick={() => void notesQuery.refetch()}>
                try again
              </Button>
            </div>
          </div>
        </Surface>
      ) : all.length === 0 ? (
        <Surface>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <span style={mutedKicker}>§ empty</span>
            <p style={emptyBody}>
              No notes yet. Hit "new note" to drop your first sticky.
            </p>
            <div>
              <Button type="button" onClick={() => setEditor({ open: true, note: null })}>
                new note
              </Button>
            </div>
          </div>
        </Surface>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          accessibility={{ announcements }}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onDragCancel={() => setActiveId(null)}
        >
          {pinned.length > 0 && (
            <NoteSection
              label="pinned"
              notes={pinned}
              onClick={(n) => setEditor({ open: true, note: n })}
              onDelete={requestDelete}
              onTogglePin={(n) => pinMut.mutate(n)}
            />
          )}
          {others.length > 0 && (
            <NoteSection
              label={pinned.length > 0 ? 'notes' : 'all notes'}
              notes={others}
              onClick={(n) => setEditor({ open: true, note: n })}
              onDelete={requestDelete}
              onTogglePin={(n) => pinMut.mutate(n)}
            />
          )}
          {portalReady &&
            createPortal(
              <DragOverlay
                dropAnimation={
                  reducedMotion
                    ? null
                    : { duration: 200, easing: 'cubic-bezier(0.16,1,0.3,1)' }
                }
              >
                {activeNote ? <NoteBody note={activeNote} ghost /> : null}
              </DragOverlay>,
              document.body,
            )}
        </DndContext>
      )}

      {/* Portalled for the same reason as the Hot Plate's: both are
          `position: fixed`, and Shell's `.motion-page` wrapper carries a
          transform + filter for 460ms after every route change, which would
          make it their containing block. `data-mod` rides along so the
          accent survives the move out of `.mn-root`. */}
      {portalReady &&
        undoNote &&
        createPortal(
          <div role="status" className="mn-undo" data-mod={mod} style={styleVars}>
            <span style={undoText}>
              Removed “{truncate(undoNote.title || undoNote.content || 'note', 42)}”.
            </span>
            <button type="button" onClick={undoDelete} style={undoBtn}>
              undo
            </button>
          </div>,
          document.body,
        )}

      {portalReady &&
        editor.open &&
        createPortal(
          <div data-mod={mod} style={styleVars} className="mn-root">
            <NoteEditor
              state={editor}
              queryKey={queryKey}
              onClose={() => setEditor(CLOSED_EDITOR)}
            />
          </div>,
          document.body,
        )}
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────

function NoteSection({
  label,
  notes,
  onClick,
  onDelete,
  onTogglePin,
}: {
  label: string;
  notes: UserNote[];
  onClick: (n: UserNote) => void;
  onDelete: (n: UserNote) => void;
  onTogglePin: (n: UserNote) => void;
}) {
  const ids = useMemo(() => notes.map((n) => n.id ?? '').filter(Boolean), [notes]);
  return (
    <section style={{ marginBottom: 26 }}>
      <span style={sectionKicker}>§ {label}</span>
      <SortableContext items={ids} strategy={rectSortingStrategy}>
        <div className="mn-grid">
          {notes.map((n, i) => (
            <SortableNote
              // Never `Math.random()` — an id-less row would get a fresh
              // key every render, remount, and lose focus mid-typing.
              key={n.id || `mn-row-${i}`}
              note={n}
              onClick={() => onClick(n)}
              onDelete={() => onDelete(n)}
              onTogglePin={() => onTogglePin(n)}
            />
          ))}
        </div>
      </SortableContext>
    </section>
  );
}

/** Stop an interactive child from starting a drag or opening the editor. */
function stopDragProps(): {
  onPointerDown: (e: ReactPointerEvent) => void;
  onMouseDown: (e: ReactMouseEvent) => void;
  onTouchStart: (e: ReactTouchEvent) => void;
  onKeyDown: (e: ReactKeyboardEvent) => void;
} {
  return {
    onPointerDown: (e) => e.stopPropagation(),
    onMouseDown: (e) => e.stopPropagation(),
    onTouchStart: (e) => e.stopPropagation(),
    onKeyDown: (e) => e.stopPropagation(),
  };
}

function SortableNote({
  note,
  onClick,
  onDelete,
  onTogglePin,
}: {
  note: UserNote;
  onClick: () => void;
  onDelete: () => void;
  onTogglePin: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({
      id: note.id ?? '',
      disabled: !note.id,
      // Same reasoning as the Hot Plate card: a note carries three real
      // buttons (edit / pin / delete), so the draggable wrapper must not
      // claim `role="button"` around them.
      attributes: { role: 'group', roleDescription: 'draggable note' },
    });
  const reducedMotion = usePrefersReducedMotion();

  return (
    <div
      ref={setNodeRef}
      style={{
        // `transform` is what slides the OTHER notes aside to open a gap;
        // `willChange` promotes that slide onto the compositor.
        transform: CSS.Translate.toString(transform),
        transition: reducedMotion ? undefined : transition,
        willChange: transform ? 'transform' : undefined,
        opacity: isDragging ? 0 : 1,
      }}
      {...attributes}
      {...listeners}
      onClick={() => {
        if (isDragging) return;
        onClick();
      }}
    >
      <NoteBody
        note={note}
        onDelete={onDelete}
        onTogglePin={onTogglePin}
        onEdit={onClick}
      />
    </div>
  );
}

function NoteBody({
  note,
  ghost = false,
  onDelete,
  onTogglePin,
  onEdit,
}: {
  note: UserNote;
  ghost?: boolean;
  onDelete?: () => void;
  onTogglePin?: () => void;
  onEdit?: () => void;
}) {
  const palette = notePalette(note.color);
  return (
    <article
      className={`mn-card${ghost ? ' mn-card--ghost' : ''}`}
      style={{ background: palette.bg, borderColor: palette.border }}
    >
      <span style={{ ...colorDot, background: palette.dot }} />
      {note.title && <h3 style={{ ...noteTitle, color: palette.title }}>{note.title}</h3>}
      {note.content && (
        <p style={{ ...noteBody, color: palette.body }}>
          {note.content.length > 240 ? `${note.content.slice(0, 240)}…` : note.content}
        </p>
      )}
      {!ghost && (onTogglePin || onEdit || onDelete) && (
        <div className="mn-card__actions">
          {onEdit && (
            <IconBtn
              label={`Edit note ${note.title ?? ''}`.trim()}
              title="Edit"
              onClick={onEdit}
              glyph="✎"
            />
          )}
          {onTogglePin && (
            <IconBtn
              label={note.pinned ? 'Unpin' : 'Pin'}
              title={note.pinned ? 'Unpin' : 'Pin'}
              onClick={onTogglePin}
              glyph={note.pinned ? '★' : '☆'}
              on={!!note.pinned}
            />
          )}
          {onDelete && (
            <IconBtn
              label="Delete"
              title="Delete"
              onClick={onDelete}
              glyph="×"
              tone="danger"
            />
          )}
        </div>
      )}
    </article>
  );
}

function IconBtn({
  label,
  title,
  onClick,
  glyph,
  tone,
  on,
}: {
  label: string;
  title: string;
  onClick: () => void;
  glyph: string;
  tone?: 'danger';
  on?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={title}
      className={`mn-iconbtn${tone === 'danger' ? ' mn-iconbtn--danger' : ''}${on ? ' mn-iconbtn--on' : ''}`}
      {...stopDragProps()}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      {glyph}
    </button>
  );
}

function NoteEditor({
  state,
  queryKey,
  onClose,
}: {
  state: EditorState;
  /** Single cache key — notes are user-scoped, not per-module. */
  queryKey: readonly ['my-notes'];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [color, setColor] = useState<NoteColor>('yellow');
  const [pinned, setPinned] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!state.open) return;
    const n = state.note;
    setTitle(n?.title ?? '');
    setContent(n?.content ?? '');
    setColor(((n?.color as NoteColor) ?? 'yellow') as NoteColor);
    setPinned(n?.pinned ?? false);
    setFormError(null);
  }, [state]);

  // A11Y-001 wave 3 — focus trap + Esc-to-close.
  const dialogRef = useRef<HTMLDivElement | null>(null);
  useFocusTrap(dialogRef, { open: state.open, onEscape: onClose });

  const saveMut = useMutation({
    mutationFn: (req: SaveNoteRequest) => saveNote(req),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey });
      onClose();
    },
    onError: (err: unknown) =>
      setFormError(err instanceof Error ? err.message : 'Could not save the note.'),
  });

  if (!state.open) return null;

  const onSubmit = () => {
    setFormError(null);
    if (title.trim().length === 0 && content.trim().length === 0) {
      setFormError('Add a title or some content.');
      return;
    }
    saveMut.mutate({
      id: state.note?.id ?? null,
      title: title.trim() || null,
      content: content.trim() || null,
      color,
      pinned,
    });
  };

  return (
    // Canonical opaque overlay surfaces per CLAUDE.md — `.hub-scrim` owns
    // the strong `--scrim` backdrop and `.hub-modal` the opaque
    // `--surface-2` panel. The note colour survives as a 10% tint.
    <div
      className="hub-scrim mn-scrim-in"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Edit note"
        className="hub-modal mn-panel-in"
        style={{ background: editorTint(color) }}
      >
        <div style={editorPad}>
          <input
            type="text"
            value={title}
            maxLength={200}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Title"
            style={editorTitle}
          />
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Take a note."
            rows={6}
            style={editorBody}
          />
          <div style={editorControls}>
            <div style={colorRow}>
              {NOTE_COLORS.map((c) => {
                const pal = notePalette(c);
                const active = c === color;
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setColor(c)}
                    aria-label={`${c} color`}
                    aria-pressed={active}
                    style={{
                      ...colorSwatch,
                      background: pal.dot,
                      outline: active
                        ? `2px solid color-mix(in oklab, ${pal.dot} 80%, transparent)`
                        : 'none',
                      outlineOffset: 2,
                    }}
                  />
                );
              })}
            </div>
            <label style={pinToggle}>
              <input
                type="checkbox"
                checked={pinned}
                onChange={(e) => setPinned(e.target.checked)}
              />
              <span>pin</span>
            </label>
          </div>
          {formError && (
            <div role="alert" style={editorErr}>
              {formError}
            </div>
          )}
          <div style={editorFooter}>
            <Button variant="ghost" type="button" onClick={onClose}>
              cancel
            </Button>
            <Button type="button" onClick={onSubmit} disabled={saveMut.isPending}>
              {saveMut.isPending ? 'saving.' : 'save note'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Surface({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        padding: 28,
        borderRadius: 'var(--hm-radius-lg)',
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
      }}
    >
      {children}
    </div>
  );
}

function truncate(s: string, max: number): string {
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

// ── Styles ──────────────────────────────────────────────────────────

const heroWrap: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  gap: 24,
  flexWrap: 'wrap',
  marginBottom: 22,
};
const kicker: CSSProperties = {
  fontFamily: 'var(--hm-mono)',
  fontSize: 11,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  color: 'var(--accent)',
  fontWeight: 600,
};
const heroTitle: CSSProperties = {
  fontFamily: 'var(--hm-font)',
  fontSize: 40,
  fontWeight: 500,
  letterSpacing: '-0.025em',
  color: 'var(--text)',
  lineHeight: 1.06,
  margin: '8px 0 0',
};
const heroSub: CSSProperties = {
  marginTop: 10,
  maxWidth: 540,
  color: 'var(--text-muted)',
  fontSize: 13.5,
  lineHeight: 1.55,
};
const sectionKicker: CSSProperties = {
  display: 'block',
  marginBottom: 12,
  fontFamily: 'var(--hm-mono)',
  fontSize: 10,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  color: 'var(--text-dim)',
  fontWeight: 600,
};
const colorDot: CSSProperties = {
  display: 'inline-block',
  width: 8,
  height: 8,
  borderRadius: '50%',
};
const noteTitle: CSSProperties = {
  margin: 0,
  fontFamily: 'var(--hm-font)',
  fontSize: 15,
  fontWeight: 600,
  letterSpacing: '-0.01em',
  paddingRight: 76,
};
const noteBody: CSSProperties = {
  margin: 0,
  fontSize: 13,
  lineHeight: 1.55,
  whiteSpace: 'pre-wrap',
};
const editorPad: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  padding: 20,
};
const editorTitle: CSSProperties = {
  fontFamily: 'var(--hm-font)',
  fontSize: 18,
  fontWeight: 600,
  color: 'var(--text)',
  background: 'transparent',
  border: 'none',
  outline: 'none',
  padding: 0,
};
const editorBody: CSSProperties = {
  fontFamily: 'var(--hm-font)',
  fontSize: 14,
  lineHeight: 1.55,
  color: 'var(--text-muted)',
  background: 'transparent',
  border: 'none',
  outline: 'none',
  resize: 'vertical',
  minHeight: 140,
  padding: 0,
};
const editorControls: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 10,
};
const colorRow: CSSProperties = {
  display: 'flex',
  gap: 6,
};
const colorSwatch: CSSProperties = {
  width: 18,
  height: 18,
  borderRadius: '50%',
  border: 'none',
  cursor: 'pointer',
};
const pinToggle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontFamily: 'var(--hm-mono)',
  fontSize: 11,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
  cursor: 'pointer',
};
const editorErr: CSSProperties = {
  padding: '8px 10px',
  borderRadius: 'var(--hm-radius)',
  background: 'color-mix(in oklab, var(--heat) 14%, transparent)',
  border: '1px solid color-mix(in oklab, var(--heat) 30%, transparent)',
  color: 'var(--heat)',
  fontFamily: 'var(--hm-mono)',
  fontSize: 11,
};
const editorFooter: CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 8,
  marginTop: 6,
};
const mutedKicker: CSSProperties = {
  fontFamily: 'var(--hm-mono)',
  fontSize: 11,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
};
const emptyBody: CSSProperties = {
  margin: 0,
  color: 'var(--text-muted)',
  fontSize: 13.5,
};
const bannerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  padding: '8px 12px',
  marginBottom: 14,
  background: 'color-mix(in oklab, var(--heat) 12%, var(--surface-2))',
  color: 'var(--heat)',
  border: '1px solid color-mix(in oklab, var(--heat) 35%, transparent)',
  borderRadius: 'var(--hm-radius)',
  fontFamily: 'var(--hm-mono)',
  fontSize: 12,
  lineHeight: 1.4,
};
const bannerDismiss: CSSProperties = {
  flex: '0 0 auto',
  background: 'transparent',
  border: 'none',
  color: 'inherit',
  cursor: 'pointer',
  fontSize: 18,
  lineHeight: 1,
  padding: '0 4px',
};
const undoText: CSSProperties = {
  fontFamily: 'var(--hm-font)',
  fontSize: 13,
  color: 'var(--text)',
  minWidth: 0,
};
const undoBtn: CSSProperties = {
  flex: '0 0 auto',
  fontFamily: 'var(--hm-mono)',
  fontSize: 10.5,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  fontWeight: 700,
  padding: '5px 14px',
  color: 'var(--bg)',
  background: 'var(--accent)',
  border: 'none',
  borderRadius: 'var(--hm-radius-pill)',
  cursor: 'pointer',
};
